import { createRouter, createWebHistory } from 'vue-router'
import Home from '../views/Home.vue'
import DeckBuilder from '../views/DeckBuilder.vue'
import DeckPicker from '../views/DeckPicker.vue'
import Recycle from '../views/Recycle.vue'
import Account from '../views/Account.vue'
import Lobby from '../views/Lobby.vue'
import Rooms from '../views/Rooms.vue'
import Watch from '../views/Watch.vue'
import OnlineDuel from '../views/OnlineDuel.vue'
import Packs from '../views/Packs.vue'
import DailyMissions from '../views/DailyMissions.vue'
import Rank from '../views/Rank.vue'
import Collection from '../views/Collection.vue'
import Login from '../views/Login.vue'
import Register from '../views/Register.vue'
import ForgotPassword from '../views/ForgotPassword.vue'
import ResetPassword from '../views/ResetPassword.vue'
import { useCollectionStore } from '../stores/collection'
import { useAuthStore } from '../stores/auth'
import { ensurePlayableDeckList } from '../game/localDeck'
import { hydrateAccount, refreshDecksIfClean, settleDeckWrites } from '../account/sync'
import { adminClientPath } from '../views/admin/path'
import { buildIsStale, checkForNewBuild } from '../utils/staleBuild'

const DUEL_ROUTE_NAMES = new Set(['online-duel', 'lobby', 'rooms', 'duel'])
const PUBLIC_ROUTE_NAMES = new Set(['login', 'register', 'forgot-password'])
/** Reachable signed in or signed out. PayPal returns here after a donation. */
const OPEN_ROUTE_NAMES = new Set(['thanks', 'changelog-shots'])

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: Login,
    },
    {
      path: '/register',
      name: 'register',
      component: Register,
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: ForgotPassword,
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: ResetPassword,
    },
    {
      path: '/',
      name: 'home',
      component: Home
    },
    {
      path: '/thanks',
      name: 'thanks',
      component: () => import('../views/Thanks.vue'),
    },
    {
      path: '/packs',
      name: 'packs',
      component: Packs
    },
    {
      path: '/missions',
      name: 'daily-missions',
      component: DailyMissions
    },
    {
      path: '/rank',
      name: 'rank',
      component: Rank
    },
    {
      path: '/collection',
      name: 'collection',
      component: Collection
    },
    {
      path: '/deck-builder',
      name: 'deck-builder',
      component: DeckPicker
    },
    {
      path: '/deck-builder/recycle',
      name: 'recycle',
      component: Recycle,
    },
    {
      path: '/deck-builder/:deckId',
      name: 'deck-editor',
      component: DeckBuilder
    },
    {
      path: '/guides',
      redirect: { name: 'home' },
    },
    {
      path: '/changelog',
      name: 'changelog',
      component: () => import('../views/Changelog.vue'),
    },
    {
      path: '/banlist',
      name: 'banlist',
      component: () => import('../views/Banlist.vue'),
    },
    {
      path: '/meta',
      name: 'meta',
      component: () => import('../views/Meta.vue'),
    },
    {
      path: '/tournaments',
      name: 'tournaments',
      component: () => import('../views/Tournaments.vue'),
    },
    {
      path: '/tournaments/:id',
      name: 'tournament-detail',
      component: () => import('../views/TournamentDetail.vue'),
    },
    {
      path: '/faq',
      name: 'faq',
      component: () => import('../views/Faq.vue'),
    },
    {
      path: '/mailbox',
      name: 'mailbox',
      component: () => import('../views/Mailbox.vue'),
    },
    {
      path: '/settings',
      name: 'settings',
      component: Account,
    },
    {
      path: '/lobby',
      name: 'lobby',
      component: Lobby
    },
    {
      path: '/rooms',
      name: 'rooms',
      component: Rooms
    },
    {
      path: '/watch',
      name: 'watch',
      component: Watch
    },
    {
      path: '/online-duel',
      name: 'online-duel',
      component: OnlineDuel
    },
    {
      path: '/profile',
      name: 'profile',
      component: Account,
    },
    {
      path: '/friends',
      name: 'friends',
      component: () => import('../views/Friends.vue'),
    },
    {
      path: '/volver',
      name: 'return-pack',
      component: () => import('../views/ReturnPack.vue'),
    },
    {
      path: '/u/:username',
      name: 'public-profile',
      component: () => import('../views/PublicProfile.vue'),
    },
    // Must be static: a catch-all `redirect` runs before beforeEach, so a
    // lazy addRoute never sees /__ops on a fresh load. Non-admins bounce below.
    {
      path: adminClientPath(),
      name: 'ops',
      component: () => import('../views/admin/AdminHome.vue'),
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: { name: 'home' },
    },
  ]
})

if (import.meta.env.DEV) {
  router.addRoute({
    path: '/changelog-shots',
    name: 'changelog-shots',
    component: () => import('../views/ChangelogShots.vue'),
  })
  router.addRoute({
    path: '/test-ygopro',
    name: 'test-ygopro',
    component: () => import('../views/TestYGOPro.vue'),
  })
  router.addRoute({
    path: '/duel',
    name: 'duel',
    component: () => import('../views/Duel.vue'),
  })
  router.addRoute({
    path: '/random-deck-preview',
    name: 'random-deck-preview',
    component: () => import('../views/RandomDeckPreview.vue'),
  })
}

function safeRedirect(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  if (!raw.startsWith('/') || raw.startsWith('//')) return null
  return raw
}

router.beforeEach(async (to, from) => {
  if (OPEN_ROUTE_NAMES.has(String(to.name))) return true
  const auth = useAuthStore()
  const isOps = to.name === 'ops'
  if (auth.token) {
    if (isOps) await auth.fetchMe()
    else await auth.ensureSession()
    await hydrateAccount()
    const stayInEditor = to.name === 'deck-editor' && from.name === 'deck-editor'
    // "Use in duels" is still debounced. Finish that write before a snapshot
    // can put the previous baraja back.
    if (from.name === 'deck-editor' && !stayInEditor) await settleDeckWrites()
    if (!stayInEditor) await refreshDecksIfClean()
  }

  // A deploy landed while this tab was open. Load the new bundle between two
  // menus. Never into a duel (the lobby hands the match over in memory) and
  // never out of one here (afterEach waits for the room leave first).
  checkForNewBuild()
  if (
    buildIsStale() &&
    from.matched.length > 0 &&
    !DUEL_ROUTE_NAMES.has(String(from.name)) &&
    !DUEL_ROUTE_NAMES.has(String(to.name))
  ) {
    window.location.assign(to.fullPath)
    return false
  }

  const isPublic = PUBLIC_ROUTE_NAMES.has(String(to.name))
  const isReset = to.name === 'reset-password'
  if (!auth.token) {
    if (isPublic || isReset) return true
    const redirect = to.fullPath !== '/' ? to.fullPath : undefined
    return { name: 'login', query: redirect ? { redirect } : {} }
  }
  if (isPublic) {
    return safeRedirect(to.query.redirect) || { name: 'home' }
  }
  if (isReset) return true
  if (isOps && !auth.user?.isAdmin) {
    return { name: 'home' }
  }

  if (to.name === 'online-duel') {
    const mode = String(to.query.mode || '')
    if (mode === 'spectate' || mode === 'replay') return true
  }
  if (!DUEL_ROUTE_NAMES.has(String(to.name))) return true
  const collection = useCollectionStore()
  if (ensurePlayableDeckList()) return true
  if (collection.welcomePending) return { name: 'packs' }
  return { name: 'home', query: { needDeck: '1' } }
})

// Out of a duel on an old bundle. The unmount sends room:leave with an
// 800 ms ack; a reload before that leaves the seat held for a reconnect.
router.afterEach((to, from) => {
  if (!buildIsStale()) return
  if (!DUEL_ROUTE_NAMES.has(String(from.name)) || DUEL_ROUTE_NAMES.has(String(to.name))) return
  window.setTimeout(() => {
    if (DUEL_ROUTE_NAMES.has(String(router.currentRoute.value.name))) return
    window.location.reload()
  }, 1_000)
})

export default router 